import "./lineups.sass";
import { useEffect, useState } from "react";
import SoccerLineUp from "react-soccer-lineup";
import { getLineups, transformLineupsData } from "../../../../services/minute-a-minute/getLineups";
import { getMatch, transformMatchData } from "../../../../services/minute-a-minute/getMatch";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { useSuppressDevToolsWarning } from "../../../../hooks/useSuppressDevToolsWarning";
import { useIsMobileBreakpoint } from "../../../../hooks/useIsMobileBreakpoint";
import Loading from "../../../loading/loading";
import SubstitutesList from "./SubstitutesList";
import TeamInfo from "./TeamInfo";
import PlayerStatsModal from "./PlayerStatsModal";

export default function Lineups({ matchId = null, refresh = false, country = null }) {
  const [homeLineup, setHomeLineup] = useState(null);
  const [awayLineup, setAwayLineup] = useState(null);
  const [homeTeamName, setHomeTeamName] = useState("");
  const [awayTeamName, setAwayTeamName] = useState("");
  const [loading, setLoading] = useState(!!matchId);
  const [error, setError] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedPlayerSide, setSelectedPlayerSide] = useState(null);
  const isMobile = useIsMobileBreakpoint(480);

  // Suprimir el warning de React DevTools (bug interno de DevTools)
  useSuppressDevToolsWarning();

  // Manejar clicks en jugadores de la cancha
  const handlePlayerClick = (player) => {
    setSelectedPlayer(player);
    // Determinar si el jugador es del equipo local o visitante
    const isHomePlayer = homeLineup?.starters?.some(p => p.playerId === player.playerId);
    setSelectedPlayerSide(isHomePlayer ? "home" : "away");
  };

  // Función para obtener datos con o sin loading
  const fetchLineupsData = async (showLoading = true) => {
    if (!matchId) {
      setError("No se proporcionó el ID del partido");
      setLoading(false);
      return;
    }

    try {
      if (showLoading) setLoading(true);

      // Obtener lineups y datos del partido en paralelo
      const [lineupsData, matchResponse] = await Promise.all([
        getLineups(matchId, country),
        getMatch(matchId, country),
      ]);

      const transformedLineups = transformLineupsData(lineupsData);
      const transformedMatch = transformMatchData(matchResponse);

      setHomeLineup(transformedLineups.home);
      setAwayLineup(transformedLineups.away);
      setHomeTeamName(transformedMatch?.homeTeamInfo?.name || "Equipo Local");
      setAwayTeamName(transformedMatch?.awayTeamInfo?.name || "Equipo Visitante");
      setError(null);
    } catch (err) {
      console.error("Error cargando alineaciones:", err);
      setError(err.message || "Error cargando alineaciones");
    } finally {
      setLoading(false);
    }
  };

  // Usar hook de auto-refresh si refresh es true
  useAutoRefresh(refresh, fetchLineupsData, [matchId, refresh, country]);

  // Cargar datos iniciales si refresh es false
  useEffect(() => {
    if (!refresh) {
      fetchLineupsData(true);
    }
  }, [matchId, refresh, country]);

  /**
   * Mapea posiciones de API a roles esperados por react-soccer-lineup
   * @param {string} position - Posición desde el API
   * @returns {string} - Rol para la librería (gk, df, cm, fw, cdm, cam)
   */
  const mapPositionToRole = (position) => {
    if (!position) return "cm";

    const positionMap = {
      goalkeeper: "gk",
      defender: "df",
      midfielder: "cm",
      forward: "fw",
      "central midfielder": "cm",
      "defensive midfielder": "cdm",
      "attacking midfielder": "cam",
      striker: "fw",
      winger: "cm",
      "left back": "df",
      "right back": "df",
      "left wing": "cm",
      "right wing": "cm",
      "central defender": "df",
      "back": "df",
      "mid": "cm",
      "forward": "fw",
    };

    const lowerPosition = position.toLowerCase();
    return positionMap[lowerPosition] || "cm";
  };

  /**
   * Agrupa jugadores por rol para react-soccer-lineup
   * Estructura esperada: gk como objeto único, resto como arrays
   * @param {Array} players - Array de jugadores
   * @param {string} formation - Formación del equipo
   * @returns {Object} - Objeto con jugadores agrupados por rol
   */
  const groupPlayersByRole = (players, formation = "4-3-3") => {
    const grouped = {
      gk: null,
      df: [],
      cm: [],
      fw: [],
      cdm: [],
      cam: [],
    };

    if (!players || !Array.isArray(players)) return grouped;

    players.forEach(player => {
      const role = mapPositionToRole(player.position);
      const playerObj = {
        number: player.shirt,
        name: !isMobile && player.name,
        onClick: () => handlePlayerClick(player), // Agregar handler de click
      };

      // Si es capitán, agregamos estilos especiales
      if (player.captain) {
        playerObj.style = {
          numberBackgroundColor: "#ffd600",
          numberColor: "#333",
          borderColor: "#ffd600",
        };
      }

      if (role === "gk") {
        // GK es un objeto único
        if (!grouped.gk) {
          grouped.gk = playerObj;
        }
      } else {
        // El resto son arrays
        grouped[role].push(playerObj);
      }
    });

    // Si la formación es 5-4-1, redistribuir jugadores
    if (formation === "5-4-1") {
      // 5-4-1: 5 defensas, 4 mediocampistas, 1 delantero
      // Asegurar que hay 5 defensas
      while (grouped.df.length < 5 && grouped.cm.length > 0) {
        grouped.df.push(grouped.cm.shift());
      }

      // Asegurar que hay 4 mediocampistas
      while (grouped.cm.length < 4 && grouped.fw.length > 1) {
        grouped.cm.push(grouped.fw.shift());
      }

      // Asegurar que hay solo 1 delantero
      while (grouped.fw.length > 1) {
        grouped.cm.push(grouped.fw.pop());
      }
    }

    return grouped;
  };

  /**
   * Obtiene la formación como string (ej: "4-3-3")
   * @param {string} formation - Formación del API
   * @returns {string} - Formación validada
   */
  const getFormationString = (formation) => {
    const validFormations = ["4-3-3", "4-4-2", "4-2-3-1", "3-4-3", "5-3-2", "3-5-2", "5-4-1"];
    return validFormations.includes(formation) ? formation : "4-3-3";
  };

  if (loading) {
    return (
      <div className="lineups-container">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="lineups-container">
        <div className="error-message">Error: {error}</div>
      </div>
    );
  }

  // Validar si hay alineaciones disponibles
  // Considerar sin alineaciones si ambos equipos no tienen titulares
  const hasHomeStarters = homeLineup?.starters && Array.isArray(homeLineup.starters) && homeLineup.starters.length > 0;
  const hasAwayStarters = awayLineup?.starters && Array.isArray(awayLineup.starters) && awayLineup.starters.length > 0;

  if (!hasHomeStarters && !hasAwayStarters) {
    return (
      <div className="lineups-container">
        <div className="empty-message">No hay alineaciones disponibles</div>
      </div>
    );
  }

  return (
    <div className="lineups-container">
      {/* Cancha única con ambos equipos */}
      <div className="unified-pitch">
        <SoccerLineUp
          size="responsive"
          color="#40761c"
          pattern="none"
          borderRadius="4px"
          orientation={isMobile ? "vertical" : "horizontal"}
          homeTeamFormation={getFormationString(homeLineup?.formation)}
          awayTeamFormation={getFormationString(awayLineup?.formation)}
          homeTeam={{
            squad: groupPlayersByRole(homeLineup?.starters || [], getFormationString(homeLineup?.formation)),
            style: {
              color: '#e1241b',
              borderColor: '#ff0000',
              numberColor: '#fff',
              patternColor: '#333',
              nameColor: '#fff',
              nameBackgroundColor: '#0d0d0d4c',
              nameOverflow: 'ellipsis',
              size: 'small'
            }
          }}
          awayTeam={{
            squad: groupPlayersByRole(awayLineup?.starters || [], getFormationString(awayLineup?.formation)),
            style: {
              color: '#fff',
              borderColor: '#333',
              numberColor: '#333',
              numberBackgroundColor: '#fcfcfc',
              pattern: 'stripes',
              patternColor: '#333',
              nameColor: '#fff',
              nameBackgroundColor: '#0d0d0d4c',
              nameOverflow: 'ellipsis',
              size: 'small'
            }
          }}
        />
      </div>

      {/* Información de equipos */}
      <div className="teams-info-wrapper">
        <TeamInfo
          lineup={homeLineup}
          teamName={homeTeamName}
          captain={homeLineup && homeLineup.starters.find(p => p.captain)?.name}
          captainNumber={homeLineup && homeLineup.starters.find(p => p.captain)?.shirt}
          classModifier="home"
        />
        <TeamInfo
          lineup={awayLineup}
          teamName={awayTeamName}
          captain={awayLineup && awayLineup.starters.find(p => p.captain)?.name}
          captainNumber={awayLineup && awayLineup.starters.find(p => p.captain)?.shirt}
          classModifier="away"
        />
      </div>

      {/* Suplentes de ambos equipos en sección separada */}
      <div className="substitutes-section-wrapper">
        <SubstitutesList
          substitutes={homeLineup?.substitutes}
          teamLabel={homeTeamName}
          classModifier="home"
        />
        <SubstitutesList
          substitutes={awayLineup?.substitutes}
          teamLabel={awayTeamName}
          classModifier="away"
        />
      </div>

      {/* Modal de estadísticas del jugador */}
      <PlayerStatsModal
        player={selectedPlayer}
        isOpen={!!selectedPlayer}
        onClose={() => {
          setSelectedPlayer(null);
          setSelectedPlayerSide(null);
        }}
        side={selectedPlayerSide}
      />
    </div>
  );
}